// Quotes shown on the post-login splash screen. Ported from the demo's
// SPLASH_QUOTES array (DEMO_REFERENCE.html:6210). Each quote has an EN +
// ES variant; the splash picks one in the active UI language.
//
// Source mix: verified craft/leadership figures, barbering trade wisdom
// (folk-attributed), and brand-voice lines. The cursor is persisted in
// localStorage so consecutive logins walk through the set sequentially —
// no repeats until the full set has cycled.

export type SplashQuote = {
  text: string; // may contain "\n" for forced line breaks
  attr: string;
};

export type SplashQuoteEntry = {
  en: SplashQuote;
  es: SplashQuote;
};

export const SPLASH_QUOTES: ReadonlyArray<SplashQuoteEntry> = [
  // --- Verified quotes from craft + leadership figures ---
  {
    en: { text: '"Quality is not an act, it is a habit."', attr: '— ARISTOTLE' },
    es: { text: '"La calidad no es un acto, es un hábito."', attr: '— ARISTÓTELES' },
  },
  {
    en: { text: '"The details are not the details.\nThey make the design."', attr: '— CHARLES EAMES' },
    es: { text: '"Los detalles no son los detalles.\nHacen el diseño."', attr: '— CHARLES EAMES' },
  },
  {
    en: { text: '"Excellence is the gradual result\nof always striving to do better."', attr: '— PAT RILEY' },
    es: { text: '"La excelencia es el resultado gradual\nde siempre esforzarse por mejorar."', attr: '— PAT RILEY' },
  },
  {
    en: { text: '"Do what you do so well\nthey want to see it again\nand bring their friends."', attr: '— WALT DISNEY' },
    es: { text: '"Haz lo que haces tan bien\nque quieran verlo otra vez\ny traer a sus amigos."', attr: '— WALT DISNEY' },
  },
  {
    en: { text: '"Practice does not make perfect.\nPractice makes permanent."', attr: '— VINCE LOMBARDI' },
    es: { text: '"La práctica no te hace perfecto.\nLa práctica te hace permanente."', attr: '— VINCE LOMBARDI' },
  },
  {
    en: { text: '"You miss 100% of the shots\nyou don\'t take."', attr: '— WAYNE GRETZKY' },
    es: { text: '"Pierdes el 100% de los tiros\nque no haces."', attr: '— WAYNE GRETZKY' },
  },

  // --- Coach voices (verified) ---
  // Sports coaches whose voice fits the AI-business-coach framing the
  // app leans on. Mixed leagues + eras on purpose — no single sport
  // owns "coach."
  {
    en: { text: '"Left foot, right foot, breathe."', attr: '— PAT SUMMITT' },
    es: { text: '"Pie izquierdo, pie derecho, respira."', attr: '— PAT SUMMITT' },
  },
  {
    en: { text: '"The strength of the team\nis each individual member.\nThe strength of each member\nis the team."', attr: '— PHIL JACKSON' },
    es: { text: '"La fuerza del equipo\nes cada miembro individual.\nLa fuerza de cada miembro\nes el equipo."', attr: '— PHIL JACKSON' },
  },
  {
    en: { text: '"Don\'t let what you cannot do\ninterfere with what you can do."', attr: '— JOHN WOODEN' },
    es: { text: '"No dejes que lo que no puedes hacer\ninterfiera con lo que sí puedes."', attr: '— JOHN WOODEN' },
  },
  {
    en: { text: '"Do your job."', attr: '— BILL BELICHICK' },
    es: { text: '"Haz tu trabajo."', attr: '— BILL BELICHICK' },
  },
  {
    en: { text: '"It\'s not what you tell your players.\nIt\'s what they hear."', attr: '— RED AUERBACH' },
    es: { text: '"No es lo que les dices a tus jugadores.\nEs lo que ellos escuchan."', attr: '— RED AUERBACH' },
  },
  {
    en: { text: '"The way to get noticed\nis to do the small things well."', attr: '— TONY DUNGY' },
    es: { text: '"La forma de hacerte notar\nes hacer bien las cosas pequeñas."', attr: '— TONY DUNGY' },
  },
  {
    en: { text: '"I\'ve failed over and over\nand over again in my life.\nAnd that is why I succeed."', attr: '— MICHAEL JORDAN' },
    es: { text: '"He fracasado una y otra\ny otra vez en mi vida.\nPor eso tengo éxito."', attr: '— MICHAEL JORDAN' },
  },
  {
    en: { text: '"Talent is never enough.\nThe best players\nare the hardest workers."', attr: '— MAGIC JOHNSON' },
    es: { text: '"El talento nunca es suficiente.\nLos mejores jugadores\nson los que más trabajan."', attr: '— MAGIC JOHNSON' },
  },

  // --- Barbering-specific trade wisdom (folk-style attribution) ---
  {
    en: { text: '"A great fade hides\n100 small choices."', attr: 'FROM THE CHAIR' },
    es: { text: '"Un buen fade esconde\n100 pequeñas decisiones."', attr: 'DESDE LA SILLA' },
  },
  {
    en: { text: '"Cut the head, not the hair."', attr: 'BARBERING TRADITION' },
    es: { text: '"Corta la cabeza, no el pelo."', attr: 'TRADICIÓN BARBERA' },
  },
  {
    en: { text: '"Year 1: every cut was a fade.\nYear 5: every cut was a conversation."', attr: 'FROM THE CHAIR' },
    es: { text: '"Año 1: cada corte era un fade.\nAño 5: cada corte era una conversación."', attr: 'DESDE LA SILLA' },
  },
  {
    en: { text: '"The hair you cut today is\nsomeone else\'s first impression tomorrow."', attr: 'FROM THE CHAIR' },
    es: { text: '"El pelo que cortas hoy\nes la primera impresión de alguien mañana."', attr: 'DESDE LA SILLA' },
  },
  {
    en: { text: '"Master your craft.\nMaster your business.\nThe first feeds the second."', attr: 'FROM THE TRADE' },
    es: { text: '"Domina tu oficio.\nDomina tu negocio.\nEl primero alimenta al segundo."', attr: 'DEL OFICIO' },
  },
  {
    en: { text: '"The best barbers don\'t sell cuts.\nThey sell the way you feel\nwalking out."', attr: 'FROM THE CHAIR' },
    es: { text: '"Los mejores barberos no venden cortes.\nVenden cómo te sientes\nal salir."', attr: 'DESDE LA SILLA' },
  },
  {
    en: { text: '"Tip jar full = service nailed.\nEmpty = something missed."', attr: 'FROM THE CHAIR' },
    es: { text: '"Bote de propinas lleno = servicio impecable.\nVacío = algo se escapó."', attr: 'DESDE LA SILLA' },
  },
  {
    en: { text: '"If you\'re not 5 minutes early,\nyou\'re late."', attr: 'OLD-SCHOOL TRUTH' },
    es: { text: '"Si no llegas 5 minutos antes,\nestás tarde."', attr: 'VERDAD DE LA VIEJA ESCUELA' },
  },

  // --- Trade trivia / craft history ---
  {
    en: { text: 'First electric clippers: 1921.\nYou\'re standing on giants.', attr: 'FROM THE TRADE' },
    es: { text: 'Primer clipper eléctrico: 1921.\nEstás parado sobre gigantes.', attr: 'DEL OFICIO' },
  },
  {
    en: { text: 'The barber pole has spun\nfor 700 years. The craft has too.', attr: 'FROM THE TRADE' },
    es: { text: 'El poste del barbero ha girado\npor 700 años. El oficio también.', attr: 'DEL OFICIO' },
  },

  // --- Brand voice ---
  {
    en: { text: 'Brewed for barbers.\nBuilt from your numbers.', attr: 'BUSY' },
    es: { text: 'Hecho para barberos.\nConstruido con tus números.', attr: 'BUSY' },
  },
  {
    en: { text: 'Every Tuesday is a slow Tuesday.\nUntil it isn\'t.', attr: 'BUSY' },
    es: { text: 'Cada martes es un martes lento.\nHasta que deja de serlo.', attr: 'BUSY' },
  },
  {
    en: { text: 'Your chair has its own rhythm.\nWe just help you hear it.', attr: 'BUSY' },
    es: { text: 'Tu silla tiene su propio ritmo.\nNosotros te ayudamos a escucharlo.', attr: 'BUSY' },
  },
];

const CURSOR_KEY = 'busy_splash_cursor';

/** Picks the next quote in sequence, advancing the cursor. Persistent across
 *  app loads so the user walks through the full set before any repeats. */
export function pickNextSplashQuote(language: 'en' | 'es'): SplashQuote {
  const total = SPLASH_QUOTES.length;
  let cursor = 0;
  try {
    const stored = parseInt(window.localStorage.getItem(CURSOR_KEY) ?? '0', 10);
    if (Number.isFinite(stored) && stored >= 0) cursor = stored % total;
  } catch {
    /* localStorage unavailable */
  }
  const entry = SPLASH_QUOTES[cursor]!;
  const quote = entry[language];
  try {
    window.localStorage.setItem(CURSOR_KEY, String((cursor + 1) % total));
  } catch {
    /* ignore */
  }
  return quote;
}
